import { GROUP_PATH_DELIMITER, NULL_DISPLAY_KEY } from './grid-orchestration.constants';
import { bucketKeyToFilterValue } from './grid-orchestration.utils';

/**
 * Builds the `in` list for a discriminator bucket key, expanding display names
 * to every matching slug.
 *
 * Group headers use canonical display names (e.g. `Bridge`) while the elements
 * API filters on tile-layer / schema slugs. Sending only the display name
 * returns an empty page, which leaves the leaf table stuck on headers.
 *
 * @param groupKey - Path segment for the discriminator grouping level.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Distinct slug filter values to send in `{ in: [...] }`.
 */
export function resolveDiscriminatorInValues(
  groupKey: string,
  discriminatorLabels: Record<string, string>
): Array<string> {
  const values: Array<string> = new Array<string>();
  const seen: Set<string> = new Set<string>();
  const add = (value: string): void => {
    if (value.length === 0 || seen.has(value)) {
      return;
    }
    seen.add(value);
    values.push(value);
  };

  if (discriminatorLabels[groupKey] !== undefined) {
    add(groupKey);
  }
  const canonicalDisplay: string = discriminatorLabels[groupKey] ?? groupKey;
  for (const [slug, label] of Object.entries(discriminatorLabels)) {
    if (slug === groupKey || label === canonicalDisplay) {
      add(slug);
    }
  }
  if (values.length === 0) {
    add(groupKey);
  }
  return values;
}

/**
 * Resolves a discriminator group key to a schema slug, ignoring identity labels.
 *
 * A group key reaches this in one of three spellings: the display label (`Pavement`), the slug
 * itself (`pms_inventory`), or the raw column value the rows carry, which is the slug in a
 * different case (`PMS_Inventory`). Only the first two used to resolve, so a grid grouped on the
 * raw value addressed a bucket that does not exist and silently stopped paginating.
 *
 * @param groupKey - Slug, display name, or raw column value from a group path.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Matching slug, or the original key when none exists.
 */
export function canonicalDiscriminatorSlug(
  groupKey: string,
  discriminatorLabels: Record<string, string>
): string {
  for (const [slug, label] of Object.entries(discriminatorLabels)) {
    if (label === groupKey && slug !== groupKey) {
      return slug;
    }
  }

  const foldedKey: string = groupKey.toLowerCase();
  for (const slug of Object.keys(discriminatorLabels)) {
    if (slug.toLowerCase() === foldedKey) {
      return slug;
    }
  }
  return groupKey;
}

/**
 * Rewrites discriminator path segments from display names to a canonical slug.
 *
 * Group headers use labels such as `Pavement` while pagination buckets are keyed
 * by tile-layer slugs. Loading the display-name path creates a second primary
 * bucket whose `totalCount` is unscoped.
 *
 * @param path - Single-level value or composite `///` path from the grid.
 * @param groupFields - Ordered grouping chain.
 * @param discriminatorField - Host discriminator field.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Path with discriminator segments rewritten to slugs.
 */
export function canonicalizeDiscriminatorBucketPath(
  path: string,
  groupFields: ReadonlyArray<string>,
  discriminatorField: string,
  discriminatorLabels: Record<string, string>
): string {
  if (path.length === 0 || discriminatorField.length === 0) {
    return path;
  }
  const segments: Array<string> = path.split(GROUP_PATH_DELIMITER);
  const rewritten: Array<string> = segments.map((segment: string, index: number): string => {
    const field: string | undefined = groupFields[index];
    if (field !== discriminatorField) {
      return segment;
    }
    return canonicalDiscriminatorSlug(segment, discriminatorLabels);
  });
  return rewritten.join(GROUP_PATH_DELIMITER);
}

/**
 * Resolves a requested bucket path to the key pagination actually stores it under.
 *
 * The same bucket is spelled several ways — the display label (`Pavement`), the schema slug
 * (`pms_inventory`), and the raw column value the rows carry (`PMS_Inventory`) — and which one a
 * caller holds depends on where it came from. Translating between them through the label map only
 * works once the discriminator metadata has loaded, and silently does nothing before that.
 *
 * Matching against the keys that exist avoids that dependency entirely: whatever the source of the
 * difference, the bucket either is in the map or is genuinely absent. Use this at every point that
 * looks a bucket up by a path originating outside pagination.
 *
 * @param path - Requested bucket path, in any spelling.
 * @param existingKeys - Keys currently present in the pagination map.
 * @returns The matching existing key, or null when no bucket corresponds to the path.
 */
export function resolveExistingBucketKey(path: string, existingKeys: Iterable<string>): string | null {
  const foldedPath: string = path.toLowerCase();
  let foldedMatch: string | null = null;
  for (const key of existingKeys) {
    if (key === path) {
      return key;
    }
    if (foldedMatch === null && key.toLowerCase() === foldedPath) {
      foldedMatch = key;
    }
  }
  return foldedMatch;
}

/**
 * Rewrites discriminator path segments from canonical slugs back to display names.
 *
 * The inverse of {@link canonicalizeDiscriminatorBucketPath}. Pagination is keyed by slug, while
 * the group tree addresses its nodes by display name, so per-bucket view state published only
 * under the slug is invisible to the tree and its loading state never renders.
 *
 * @param path - Single-level value or composite `///` pagination key.
 * @param groupFields - Ordered grouping chain.
 * @param discriminatorField - Host discriminator field.
 * @param discriminatorLabels - Slug → display name map.
 * @returns Path with discriminator segments rewritten to display names.
 */
export function displayDiscriminatorBucketPath(
  path: string,
  groupFields: ReadonlyArray<string>,
  discriminatorField: string,
  discriminatorLabels: Record<string, string>
): string {
  if (path.length === 0 || discriminatorField.length === 0) {
    return path;
  }
  const segments: Array<string> = path.split(GROUP_PATH_DELIMITER);
  const rewritten: Array<string> = segments.map((segment: string, index: number): string => {
    if (groupFields[index] !== discriminatorField) {
      return segment;
    }
    return discriminatorLabels[segment] ?? segment;
  });
  return rewritten.join(GROUP_PATH_DELIMITER);
}

/**
 * True when a primary pagination key is a discriminator display name that aliases
 * an existing slug bucket.
 *
 * @param key - Primary-level pagination key (no path delimiter).
 * @param primaryKeys - All primary pagination keys currently in the map.
 * @param discriminatorLabels - Slug → display name map.
 * @returns True when `key` should be ignored in favor of its slug bucket.
 */
export function isAliasedDiscriminatorPaginationKey(
  key: string,
  primaryKeys: ReadonlySet<string>,
  discriminatorLabels: Record<string, string>
): boolean {
  for (const [slug, label] of Object.entries(discriminatorLabels)) {
    if (slug !== key && label === key && primaryKeys.has(slug)) {
      return true;
    }
  }
  return false;
}

/**
 * Discriminator `{ in: [...] }` clause, preferring selected types that match the bucket.
 *
 * @param groupKey - Path segment for the discriminator grouping level.
 * @param lastGridWhere - Current grid where clause (may already list selected slugs).
 * @param discriminatorField - Host discriminator field.
 * @param discriminatorLabels - Slug → display name map.
 * @returns HotChocolate `in` filter.
 */
export function discriminatorInClause(
  groupKey: string,
  lastGridWhere: Record<string, unknown>,
  discriminatorField: string,
  discriminatorLabels: Record<string, string>
): { in: Array<string> } {
  const expanded: Array<string> = resolveDiscriminatorInValues(groupKey, discriminatorLabels);
  const selected: Array<string> = extractDiscriminatorInValues(lastGridWhere, discriminatorField);
  if (selected.length === 0) {
    return { in: expanded };
  }

  const matched: Array<string> = selected.filter((type: string): boolean => {
    return expanded.includes(type) || discriminatorLabels[type] === groupKey;
  });
  return { in: matched.length > 0 ? matched : expanded };
}

/**
 * Reads discriminator-field `in` values from a HotChocolate-style where clause.
 *
 * @param where - Where clause to inspect.
 * @param discriminatorField - Host discriminator field.
 * @returns Selected type strings, or empty when the clause is absent.
 */
export function extractDiscriminatorInValues(
  where: Record<string, unknown>,
  discriminatorField: string
): Array<string> {
  if (discriminatorField.length === 0) {
    return new Array<string>();
  }
  const clause: unknown = where[discriminatorField];
  if (typeof clause !== 'object' || clause === null || !('in' in clause)) {
    return new Array<string>();
  }
  const types: unknown = (clause as { in: unknown }).in;
  if (!Array.isArray(types)) {
    return new Array<string>();
  }
  return types.filter((type: unknown): type is string => typeof type === 'string');
}

/**
 * Builds the scoped `where` clause for a bucket path.
 *
 * @param path - Single-level value or composite `///` path.
 * @param groupByFields - Ordered grouping chain.
 * @param primaryGroupField - Outermost group field, or null.
 * @param lastGridWhere - Current grid where clause.
 * @param discriminatorField - Host discriminator field, or empty.
 * @param discriminatorLabels - Slug → display name map.
 */
export function buildWhereFromPath(
  path: string,
  groupByFields: ReadonlyArray<string>,
  primaryGroupField: string | null,
  lastGridWhere: Record<string, unknown>,
  discriminatorField: string,
  discriminatorLabels: Record<string, string> = {}
): Record<string, unknown> {
  const segments: Array<string> = path.split(GROUP_PATH_DELIMITER);
  const where: Record<string, unknown> = { ...lastGridWhere };

  if (groupByFields.length <= 1) {
    const field: string | null = primaryGroupField;
    const filterValue: string | null = bucketKeyToFilterValue(path, NULL_DISPLAY_KEY);
    if (discriminatorField.length > 0 && field === discriminatorField) {
      where[discriminatorField] =
        filterValue === null
          ? { eq: null }
          : discriminatorInClause(path, lastGridWhere, discriminatorField, discriminatorLabels);
    } else if (field !== null) {
      where[field] = filterValue === null ? { eq: null } : { eq: filterValue };
    }
    return where;
  }

  for (let i: number = 0; i < groupByFields.length && i < segments.length; i++) {
    const field: string = groupByFields[i];
    const value: string = segments[i];
    const filterValue: string | null = bucketKeyToFilterValue(value, NULL_DISPLAY_KEY);
    if (discriminatorField.length > 0 && field === discriminatorField) {
      where[discriminatorField] =
        filterValue === null
          ? { eq: null }
          : discriminatorInClause(value, lastGridWhere, discriminatorField, discriminatorLabels);
    } else {
      where[field] = filterValue === null ? { eq: null } : { eq: filterValue };
    }
  }
  return where;
}

/** Whether a bucket path includes a null/Unknown segment for any grouped field. */
export function isNullBucketPath(path: string, groupByFields: ReadonlyArray<string>): boolean {
  const segments: Array<string> = path.split(GROUP_PATH_DELIMITER);
  for (let i: number = 0; i < groupByFields.length && i < segments.length; i++) {
    if (bucketKeyToFilterValue(segments[i], NULL_DISPLAY_KEY) === null) {
      return true;
    }
  }
  return false;
}
